import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  CalendarClock,
  Clock,
  Loader2,
  Info,
  Sun,
  Moon,
  Hand,
  Zap,
  Banknote,
  Users,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';
import toast from 'react-hot-toast';
import branchService from '../../../services/branchService';
import { clearBranchCache } from '../../../hooks/useBranch';

// Generate hour options (12 AM – 11 PM)
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const suffix = i < 12 ? 'AM' : 'PM';
  const display = i === 0 ? 12 : i > 12 ? i - 12 : i;
  return { value: i, label: `${display}:00 ${suffix}` };
});

const CLOSE_MODES = [
  {
    value: 'MANUAL',
    title: 'Manual',
    description: 'Staff closes the day from the dashboard when ready.',
    icon: Hand,
    color: 'teal',
  },
  {
    value: 'AUTO',
    title: 'Automatic',
    description: 'System auto-closes at the configured closeout hour.',
    icon: Zap,
    color: 'amber',
  },
];

function computeBusinessDate(closeoutHour) {
  const now = new Date();
  const currentHour = now.getHours();
  const date = new Date(now);
  if (currentHour < closeoutHour) {
    date.setDate(date.getDate() - 1);
  }
  return date.toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function DayCloseSettings({ renderActions }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branchId, setBranchId] = useState(null);
  const [closeoutHour, setCloseoutHour] = useState(4);
  const [dayCloseMode, setDayCloseMode] = useState('MANUAL');
  const [cashHandoverEnabled, setCashHandoverEnabled] = useState(false);
  const [shiftEnabled, setShiftEnabled] = useState(false);
  const [denominationEnabled, setDenominationEnabled] = useState(false);
  const [originalValues, setOriginalValues] = useState({ closeoutHour: 4, dayCloseMode: 'MANUAL', cashHandoverEnabled: false, shiftEnabled: false, denominationEnabled: false });

  const closeoutHourRef = useRef(closeoutHour);
  const dayCloseModeRef = useRef(dayCloseMode);
  const cashHandoverEnabledRef = useRef(cashHandoverEnabled);
  const shiftEnabledRef = useRef(shiftEnabled);
  const denominationEnabledRef = useRef(denominationEnabled);
  const branchIdRef = useRef(branchId);

  useEffect(() => { closeoutHourRef.current = closeoutHour; }, [closeoutHour]);
  useEffect(() => { dayCloseModeRef.current = dayCloseMode; }, [dayCloseMode]);
  useEffect(() => { cashHandoverEnabledRef.current = cashHandoverEnabled; }, [cashHandoverEnabled]);
  useEffect(() => { shiftEnabledRef.current = shiftEnabled; }, [shiftEnabled]);
  useEffect(() => { denominationEnabledRef.current = denominationEnabled; }, [denominationEnabled]);
  useEffect(() => { branchIdRef.current = branchId; }, [branchId]);

  const hasChanges = useMemo(
    () =>
      closeoutHour !== originalValues.closeoutHour ||
      dayCloseMode !== originalValues.dayCloseMode ||
      cashHandoverEnabled !== originalValues.cashHandoverEnabled ||
      shiftEnabled !== originalValues.shiftEnabled ||
      denominationEnabled !== originalValues.denominationEnabled,
    [closeoutHour, dayCloseMode, cashHandoverEnabled, shiftEnabled, denominationEnabled, originalValues]
  );

  const fetchBranch = async () => {
    setLoading(true);
    try {
      const data = await branchService.getCurrentBranch();
      if (data) {
        setBranchId(data.id);
        const hour = data.closeoutHour ?? 4;
        const mode = data.dayCloseMode || 'MANUAL';
        const handover = data.cashHandoverEnabled === true;
        const shift = data.shiftEnabled === true;
        const denom = data.denominationEnabled === true;
        setCloseoutHour(hour);
        setDayCloseMode(mode);
        setCashHandoverEnabled(handover);
        setShiftEnabled(shift);
        setDenominationEnabled(denom);
        setOriginalValues({ closeoutHour: hour, dayCloseMode: mode, cashHandoverEnabled: handover, shiftEnabled: shift, denominationEnabled: denom });
      }
    } catch (error) {
      console.error('Failed to fetch branch:', error);
      toast.error('Failed to load day close settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBranch(); }, []);

  const handleSave = async () => {
    if (!branchIdRef.current) {
      toast.error('No branch found');
      return;
    }
    setSaving(true);
    try {
      await branchService.updateBranch(branchIdRef.current, {
        closeoutHour: closeoutHourRef.current,
        dayCloseMode: dayCloseModeRef.current,
        cashHandoverEnabled: cashHandoverEnabledRef.current,
        shiftEnabled: shiftEnabledRef.current,
        denominationEnabled: denominationEnabledRef.current,
      });
      clearBranchCache();
      const newVals = {
        closeoutHour: closeoutHourRef.current,
        dayCloseMode: dayCloseModeRef.current,
        cashHandoverEnabled: cashHandoverEnabledRef.current,
        shiftEnabled: shiftEnabledRef.current,
        denominationEnabled: denominationEnabledRef.current,
      };
      setOriginalValues(newVals);
      toast.success('Day close settings saved');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save day close settings');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (renderActions) {
      renderActions({
        onSave: handleSave,
        onRefresh: fetchBranch,
        saving,
        hasChanges,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, hasChanges, shiftEnabled, denominationEnabled]);

  const businessDate = useMemo(() => computeBusinessDate(closeoutHour), [closeoutHour]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Loading day close settings...</p>
        </div>
      </div>
    );
  }

  const closeoutHint =
    closeoutHour >= 0 && closeoutHour <= 6
      ? 'Early morning — common for restaurants'
      : closeoutHour >= 22 || closeoutHour === 23
        ? 'Late night — typical for bars/pubs'
        : 'Custom closeout time';

  return (
    <div className="p-6 space-y-6">

      {/* ── Hero Banner ─────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />

        <div className="relative px-8 py-7 flex items-start justify-between gap-8">
          {/* Left – explanation */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
                <CalendarClock size={22} className="text-teal-300" />
              </div>
              <div>
                <h2 className="text-lg font-bold tracking-tight">Day Close Configuration</h2>
                <p className="text-sm text-slate-400">Define how your business day starts, ends, and what happens at closing time</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-5">
              <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-teal-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock size={14} className="text-teal-300" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-300">Business Day Boundary</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Set when one day ends and the next begins</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ShieldCheck size={14} className="text-emerald-300" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-300">Cash Reconciliation</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Track expected vs actual cash at close</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <TrendingUp size={14} className="text-indigo-300" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-300">Shift Tracking</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Per-user session tracking with cash audit</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right – live business date */}
          <div className="flex-shrink-0 text-right">
            <p className="text-[10px] font-semibold text-teal-400 uppercase tracking-widest mb-1">Current Business Date</p>
            <p className="text-xl font-bold text-white leading-tight">{businessDate}</p>
            <p className="text-xs text-slate-500 mt-1.5">
              Closeout at {HOUR_OPTIONS[closeoutHour]?.label}
            </p>
          </div>
        </div>
      </div>

      {/* ── Settings Grid — 2 columns ────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ─ Column 1: Closeout Hour ──────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Clock size={18} className="text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-sm">Closeout Hour</h3>
              <p className="text-xs text-slate-500">When does one business day end?</p>
            </div>
          </div>

          <div className="p-5 flex-1 flex flex-col justify-between">
            <p className="text-sm text-slate-600 mb-5">
              Transactions placed <strong>before</strong> this hour are assigned to the <strong>previous</strong> business day. This drives reports, sales summaries, and reconciliation.
            </p>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Select Hour
              </label>
              <select
                value={closeoutHour}
                onChange={(e) => setCloseoutHour(Number(e.target.value))}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 transition-all"
              >
                {HOUR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-slate-50 rounded-lg">
                {closeoutHour >= 5 && closeoutHour < 18 ? (
                  <Sun size={14} className="text-amber-500 flex-shrink-0" />
                ) : (
                  <Moon size={14} className="text-indigo-500 flex-shrink-0" />
                )}
                <span className="text-xs text-slate-600">{closeoutHint}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ─ Column 2: Close Mode ─────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center">
              <Zap size={18} className="text-violet-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-sm">Close Mode</h3>
              <p className="text-xs text-slate-500">How should the business day be closed?</p>
            </div>
          </div>

          <div className="p-5 flex-1 flex flex-col justify-between">
            <p className="text-sm text-slate-600 mb-5">
              <strong>Manual</strong> lets your staff decide when to close. <strong>Automatic</strong> closes the day at the closeout hour — no human intervention required.
            </p>

            <div className="grid grid-cols-2 gap-3">
              {CLOSE_MODES.map((mode) => {
                const isActive = dayCloseMode === mode.value;
                const Icon = mode.icon;
                const colorMap = {
                  teal: {
                    active: 'border-teal-400 bg-teal-50 ring-2 ring-teal-100',
                    icon: 'bg-teal-100 text-teal-600',
                    dot: 'bg-teal-500',
                  },
                  amber: {
                    active: 'border-amber-400 bg-amber-50 ring-2 ring-amber-100',
                    icon: 'bg-amber-100 text-amber-600',
                    dot: 'bg-amber-500',
                  },
                };
                const colors = colorMap[mode.color];

                return (
                  <button
                    key={mode.value}
                    onClick={() => setDayCloseMode(mode.value)}
                    className={`
                      relative text-left rounded-xl border-2 p-4 transition-all
                      ${isActive ? colors.active : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'}
                    `}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? colors.icon : 'bg-slate-100 text-slate-400'}`}>
                        <Icon size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`font-semibold text-sm ${isActive ? 'text-slate-900' : 'text-slate-700'}`}>
                            {mode.title}
                          </p>
                          {isActive && (
                            <CheckCircle2 size={14} className="text-teal-500 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{mode.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ─ Column 3: Cash Handover ──────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Banknote size={18} className="text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-sm">Cash Handover</h3>
              <p className="text-xs text-slate-500">Require cash count at end of day</p>
            </div>
            {/* Toggle in header */}
            <ToggleSwitch
              enabled={cashHandoverEnabled}
              onChange={() => setCashHandoverEnabled(!cashHandoverEnabled)}
            />
          </div>

          <div className="p-5 flex-1">
            <p className="text-sm text-slate-600 mb-4">
              Staff must enter the actual cash in the register when closing the day. The system compares it against the expected total and logs the difference.
            </p>

            <div className={`rounded-xl border p-4 transition-all ${cashHandoverEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cashHandoverEnabled ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                  {cashHandoverEnabled ? (
                    <CheckCircle2 size={16} className="text-emerald-600" />
                  ) : (
                    <Banknote size={16} className="text-slate-400" />
                  )}
                </div>
                <div>
                  <p className={`text-sm font-medium ${cashHandoverEnabled ? 'text-emerald-800' : 'text-slate-500'}`}>
                    {cashHandoverEnabled ? 'Cash handover is active' : 'Cash handover is off'}
                  </p>
                  <p className={`text-xs mt-0.5 ${cashHandoverEnabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {cashHandoverEnabled
                      ? 'Closing the day will prompt for actual cash counted.'
                      : 'The day can be closed without entering a cash count.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ─ Column 4: Shift Management ───────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Users size={18} className="text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 text-sm">Shift Management</h3>
              <p className="text-xs text-slate-500">Per-user POS sessions</p>
            </div>
            {/* Toggle in header */}
            <ToggleSwitch
              enabled={shiftEnabled}
              onChange={() => setShiftEnabled(!shiftEnabled)}
            />
          </div>

          <div className="p-5 flex-1">
            <p className="text-sm text-slate-600 mb-4">
              POS users must start a shift before taking orders. Each shift independently tracks cash, sales, and transactions for accountability.
            </p>

            <div className={`rounded-xl border p-4 transition-all ${shiftEnabled ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${shiftEnabled ? 'bg-indigo-100' : 'bg-slate-200'}`}>
                  {shiftEnabled ? (
                    <CheckCircle2 size={16} className="text-indigo-600" />
                  ) : (
                    <Users size={16} className="text-slate-400" />
                  )}
                </div>
                <div>
                  <p className={`text-sm font-medium ${shiftEnabled ? 'text-indigo-800' : 'text-slate-500'}`}>
                    {shiftEnabled ? 'Shift management is active' : 'Shift management is off'}
                  </p>
                  <p className={`text-xs mt-0.5 ${shiftEnabled ? 'text-indigo-600' : 'text-slate-400'}`}>
                    {shiftEnabled
                      ? 'POS users will see a "Start Shift" gate before accessing tables.'
                      : 'POS users can take orders without starting a shift.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Cash Denomination sub-toggle — only when shift is enabled */}
            {shiftEnabled && (
              <div className={`rounded-xl border p-4 transition-all ${denominationEnabled ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${denominationEnabled ? 'bg-amber-100' : 'bg-slate-200'}`}>
                      {denominationEnabled ? (
                        <Banknote size={16} className="text-amber-600" />
                      ) : (
                        <Banknote size={16} className="text-slate-400" />
                      )}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${denominationEnabled ? 'text-amber-800' : 'text-slate-500'}`}>
                        Cash Denomination
                      </p>
                      <p className={`text-xs mt-0.5 ${denominationEnabled ? 'text-amber-600' : 'text-slate-400'}`}>
                        {denominationEnabled
                          ? 'Staff will count cash by denomination (₹2000, ₹500, etc.) when ending a shift.'
                          : 'Staff enters a single cash amount when ending a shift.'}
                      </p>
                    </div>
                  </div>
                  <ToggleSwitch
                    enabled={denominationEnabled}
                    onChange={() => setDenominationEnabled(!denominationEnabled)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── How it works — full width ──────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
            <Info size={18} className="text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-sm">How Business Date Works</h3>
            <p className="text-xs text-slate-500">Understanding the closeout boundary</p>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <HowItWorksStep
              number="1"
              title="Closeout hour set"
              description="You choose the hour boundary — e.g. 4:00 AM."
            />
            <HowItWorksStep
              number="2"
              title="Orders before cutoff"
              description="An order at 2 AM on Jan 15 belongs to the Jan 14 business day."
            />
            <HowItWorksStep
              number="3"
              title="Orders after cutoff"
              description="An order at 5 AM on Jan 15 belongs to the Jan 15 business day."
            />
            <HowItWorksStep
              number="4"
              title="Reports align"
              description="Daily sales, summaries, and reconciliation all follow this date."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Toggle Switch ──────────────────────────────────────────── */
function ToggleSwitch({ enabled, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
        enabled ? 'bg-teal-500' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          enabled ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

/* ── How It Works Step ──────────────────────────────────────── */
function HowItWorksStep({ number, title, description }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-blue-600">
        {number}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}
